import gqlClient from "@/src/services/api/graphql.client";
import { NotificationItem } from "@/src/models/notification.model";

/**
 * `NotificationInbox` — the Blocks Data schema that drives mobile push.
 *
 * Every inserted row is turned into a visible FCM push by the `push-inbox`
 * workflow (Data Gateway insert trigger; badge = recipient's unread count). A
 * notification that is only sent to the Notifier never reaches a phone, so every
 * `sendNotification` also writes here (see notification.service.ts).
 *
 * Row-level security: recipients read/update their own rows, any signed-in user
 * may insert. Reads therefore need no `userId` filter.
 *
 * The `where` filters are written inline in the query documents on purpose:
 * gqlClient scopes a `where` *variable* to the active organization, but rows
 * fanned out by the workflow carry no OrganizationId and would be hidden.
 *
 * Transitional: the inbox is read merged with the legacy Notifier inbox
 * (`mergeInboxPage`), mirroring the mobile app's `mergeInboxPages`.
 */

export const INBOX_ID_PREFIX = "inbox:";
/** Recipient id of a broadcast row; the workflow fans it out to every user. */
export const BROADCAST_USER_ID = "*";
const DEDUPE_WINDOW_MS = 5 * 60 * 1000;

const INSERT = `
mutation InsertNotificationInbox($userId: String, $type: String, $title: String, $body: String, $targetEntityId: String, $commentId: String, $groupId: String, $actorId: String, $actorName: String, $actorAvatar: String, $createdAt: DateTime) {
  insertNotificationInbox(input: { userId: $userId, type: $type, title: $title, body: $body, targetEntityId: $targetEntityId, commentId: $commentId, groupId: $groupId, actorId: $actorId, actorName: $actorName, actorAvatar: $actorAvatar, isRead: false, createdAt: $createdAt, fanout: false }) { itemId }
}`;

const page = (unreadOnly: boolean) => `
query NotificationInbox($pageNo: Int!, $pageSize: Int!) {
  getNotificationInboxs(${unreadOnly ? "where: { isRead: { eq: false } }, " : ""}order: [{ field: "CreatedDate", direction: DESC }], paging: { pageNo: $pageNo, pageSize: $pageSize }) {
    totalCount
    items { ItemId type body targetEntityId commentId groupId actorId actorName actorAvatar isRead createdAt CreatedDate }
  }
}`;

const MARK_READ = `
mutation MarkInboxRead($id: String!) {
  updateNotificationInbox(where: { ItemId: { eq: $id } }, input: { isRead: true }) { acknowledged }
}`;

export type InboxRow = {
  ItemId: string;
  type?: string | null;
  body?: string | null;
  targetEntityId?: string | null;
  commentId?: string | null;
  groupId?: string | null;
  actorId?: string | null;
  actorName?: string | null;
  actorAvatar?: string | null;
  isRead?: boolean | null;
  createdAt?: string | null;
  CreatedDate?: string | null;
};

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v !== "" ? v : undefined;

/**
 * Writes inbox rows for a Notifier send. `userIds` containing `""` is the
 * Notifier's "everyone" form and becomes ONE broadcast row (`userId: "*"`); the
 * workflow writes the per-user copies. Never rejects: like `sendNotification`
 * it runs after the user's action has already succeeded.
 */
export const insertInboxRows = async (
  userIds: string[],
  denormalizedPayload: string,
): Promise<number> => {
  let payload: Record<string, unknown>;
  try {
    const parsed = JSON.parse(denormalizedPayload);
    if (!parsed || typeof parsed !== "object") return 0;
    payload = parsed;
  } catch {
    return 0;
  }

  const recipients = userIds.some((u) => !u)
    ? [BROADCAST_USER_ID]
    : [...new Set(userIds.filter(Boolean))];
  if (!recipients.length) return 0;

  const base = {
    type: str(payload.type),
    title: "SELISE Club",
    body: str(payload.message),
    targetEntityId:
      str(payload.targetEntityId) ??
      str(payload.postId) ??
      str(payload.eventId) ??
      str(payload.groupId),
    commentId: str(payload.commentId),
    groupId: str(payload.groupId),
    actorId: str(payload.actorId),
    actorName: str(payload.actorName),
    actorAvatar: str(payload.actorAvatar),
    createdAt: new Date().toISOString(),
  };

  let written = 0;
  try {
    const client = await gqlClient();
    for (const userId of recipients) {
      try {
        await client.request(INSERT, { ...base, userId });
        written += 1;
      } catch (error) {
        console.error("insertInboxRows: insert failed", { type: base.type, error });
      }
    }
  } catch (error) {
    console.error("insertInboxRows: gateway unavailable", error);
  }
  return written;
};

export const fetchInboxRows = async (
  pageNo: number,
  pageSize: number,
  unreadOnly = false,
): Promise<{ items: InboxRow[]; totalCount: number }> => {
  const client = await gqlClient();
  const data = (await client.request(page(unreadOnly), {
    pageNo,
    pageSize,
  })) as {
    getNotificationInboxs?: { items?: InboxRow[]; totalCount?: number };
  };
  return {
    items: data.getNotificationInboxs?.items ?? [],
    totalCount: data.getNotificationInboxs?.totalCount ?? 0,
  };
};

export const markInboxRowRead = async (itemId: string) => {
  const client = await gqlClient();
  await client.request(MARK_READ, { id: itemId });
};

/** Presents an inbox row in the Notifier shape the UI already renders. */
export const inboxRowToNotification = (row: InboxRow): NotificationItem => {
  const target = row.targetEntityId ?? undefined;
  return {
    id: `${INBOX_ID_PREFIX}${row.ItemId}`,
    denormalizedPayload: JSON.stringify({
      type: row.type ?? "general",
      message: row.body ?? "",
      targetEntityId: target,
      postId: target,
      eventId: target,
      commentId: row.commentId ?? undefined,
      groupId: row.groupId ?? undefined,
      actorId: row.actorId ?? undefined,
      actorName: row.actorName ?? "",
      actorAvatar: row.actorAvatar ?? undefined,
    }),
    isRead: !!row.isRead,
    createdTime: row.createdAt ?? row.CreatedDate ?? "",
  };
};

const key = (n: NotificationItem) => {
  try {
    const p = JSON.parse(n.denormalizedPayload) ?? {};
    return [
      p.type ?? "",
      p.targetEntityId ?? p.postId ?? p.eventId ?? p.groupId ?? "",
      p.actorId ?? "",
    ].join("\u0000");
  } catch {
    return null;
  }
};

const time = (n: NotificationItem) => {
  const t = Date.parse(n.createdTime);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Merges inbox rows into a Notifier page, newest first.
 *
 * A Notifier row and an inbox row with the same type, target and actor created
 * within 5 minutes are one notification (both stores are written during the
 * transition). The Notifier row is kept — its payload carries extra fields such
 * as `membershipId` that the inbox schema does not — under the id
 * `inbox:<itemId>|<notifierId>`, so marking it read clears both. It counts as
 * read only when both copies are.
 */
export const mergeInboxPage = (
  legacy: NotificationItem[],
  inbox: InboxRow[],
): { items: NotificationItem[]; duplicates: number; duplicatesUnread: number } => {
  const used = new Set<number>();
  let duplicates = 0;
  let duplicatesUnread = 0;
  const merged: NotificationItem[] = [];

  for (const row of inbox) {
    const item = inboxRowToNotification(row);
    const k = key(item);
    const t = time(item);
    const match = legacy.findIndex(
      (l, i) =>
        !used.has(i) &&
        k !== null &&
        key(l) === k &&
        Math.abs(time(l) - t) <= DEDUPE_WINDOW_MS,
    );
    if (match === -1) {
      merged.push(item);
      continue;
    }
    used.add(match);
    duplicates += 1;
    const l = legacy[match];
    if (!l.isRead || !item.isRead) duplicatesUnread += 1;
    merged.push({ ...l, id: `${item.id}|${l.id}`, isRead: l.isRead && item.isRead });
  }

  legacy.forEach((l, i) => {
    if (!used.has(i)) merged.push(l);
  });
  merged.sort((a, b) => time(b) - time(a));
  return { items: merged, duplicates, duplicatesUnread };
};
